import { useLayoutEffect, useRef, useState } from 'react';
import { NewDatePicker } from '../PIM/common/NewDatePicker';
import { NewInput } from '../PIM/common/NewInput';
import { NewSelectMultiSelect } from '../PIM/common/NewSelectMultiSelect';
import { SaveIcon } from '../../bombiix-icon';

/**
 * Fill colour applied to a segment when it is the selected one (Figma node
 * 7968:139640). `green` is the default — `red` marks a destructive status such
 * as Rejected. Set per option rather than inferred from the label, so the
 * colour survives translation and custom status vocabularies.
 */
export type ApprovalOptionVariant = 'green' | 'red';

export interface ApprovalOption {
  label: string;
  value: string | number;
  variant?: ApprovalOptionVariant;
}

export interface ApprovalSelectOption {
  label: string;
  value: string | number;
}

export interface BombiixApprovalBarProps {
  /**
   * The segmented approval choices. Caller supplies the labels, values and
   * selected-fill `variant`, so the bar never assumes a status vocabulary the
   * API doesn't accept — pass two options (Approved / Rejected) or three (with
   * "Approved with comments"), as the endpoint allows.
   */
  options: ApprovalOption[];
  /** Currently selected approval value; `null` renders the group unselected. */
  value?: string | number | null;
  onChange?: (_value: string | number) => void;

  showStatusType?: boolean;
  statusTypeOptions?: ApprovalSelectOption[];
  statusType?: ApprovalSelectOption | null;
  onStatusTypeChange?: (_option: ApprovalSelectOption | null) => void;
  statusTypePlaceholder?: string;

  showDatePicker?: boolean;
  date?: Date | null;
  onDateChange?: (_date: Date | null) => void;
  datePlaceholder?: string;

  showSupplier?: boolean;
  supplierOptions?: ApprovalSelectOption[];
  supplier?: ApprovalSelectOption | null;
  onSupplierChange?: (_option: ApprovalSelectOption | null) => void;
  supplierPlaceholder?: string;

  showComment?: boolean;
  comment?: string;
  onCommentChange?: (_comment: string) => void;
  commentMaxLength?: number;
  commentPlaceholder?: string;
  /** Shows the `nn/mm` counter inside the comment field. */
  showCommentCounter?: boolean;
  /**
   * Marks the comment field invalid — red border + alert icon (the `NewInput`
   * `invalid` state). The bar owns no validation itself; the caller decides
   * when a comment is required and passes the result here.
   */
  commentInvalid?: boolean;

  onSave?: () => void;
  saveDisabled?: boolean;
  /** Disables every control at once — for read-only / archived records. */
  disabled?: boolean;
  className?: string;
}

const DEFAULT_COMMENT_MAX_LENGTH = 80;

/**
 * The bar usually sits at the bottom of a page or inside a modal footer, where
 * an ancestor's `overflow`/`transform` would clip an upward-opening menu. Both
 * the selects and the date picker therefore render into <body>. react-datepicker
 * creates this node itself when the id isn't already in the DOM.
 */
const DATE_PORTAL_ID = 'bombiix-approval-bar-datepicker-portal';
const MENU_PORTAL_TARGET = typeof document !== 'undefined' ? document.body : null;

/**
 * BombiixApprovalBar — Bombiix master-design approval bar (Figma node 9309:561030).
 * A bottom-of-page action row: optional status-type select, a segmented
 * approval control, optional date picker, optional supplier select, an
 * optional comment field, and a save button.
 *
 * Fully controlled and presentational — it holds no state, fires no requests,
 * and performs no validation. The parent owns every value and decides what a
 * status means, which keeps the bar reusable across modules whose approval
 * APIs disagree.
 *
 * Each field is toggled by its own `show*` prop; the row stays correctly
 * spaced with any subset visible. Positioning is the caller's choice via
 * `className` — pass `bombiix-approval-bar--fixed` to pin it to the bottom of
 * the page (spanning the viewport minus the collapsed sidebar), or leave it
 * unset to render in flow, e.g. inside a modal footer.
 *
 * Styling lives in `assets/scss/master-design/_approval-bar.scss` — no
 * per-component CSS.
 *
 * @example
 * <BombiixApprovalBar
 *   options={[
 *     { label: 'Approved', value: 1 },
 *     { label: 'Rejected', value: 0 },
 *   ]}
 *   value={status}
 *   onChange={setStatus}
 *   showDatePicker
 *   date={date}
 *   onDateChange={setDate}
 *   onSave={handleSave}
 * />
 */
function BombiixApprovalBar({
  options,
  value = null,
  onChange,
  showStatusType,
  statusTypeOptions,
  statusType,
  onStatusTypeChange,
  statusTypePlaceholder = 'Select Status Type',
  showDatePicker,
  date,
  onDateChange,
  datePlaceholder = 'Date Picker',
  showSupplier,
  supplierOptions,
  supplier,
  onSupplierChange,
  supplierPlaceholder = 'Select Supplier',
  showComment,
  comment = '',
  onCommentChange,
  commentMaxLength = DEFAULT_COMMENT_MAX_LENGTH,
  commentPlaceholder = 'Enter Status Comments',
  showCommentCounter,
  commentInvalid,
  onSave,
  saveDisabled,
  disabled,
  className,
}: BombiixApprovalBarProps) {
  const barClass = ['bombiix-approval-bar', className].filter(Boolean).join(' ');
  // `exactOptionalPropertyTypes` is on, so an optional `boolean | undefined`
  // can't be forwarded to a `disabled?: boolean` prop — coerce to a definite
  // boolean at the boundary.
  const isDisabled = disabled === true;

  const segmentsRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);
  const activeIndex = options.findIndex((option) => option.value === value);

  /**
   * Measure the selected segment so the thumb can slide to it. Segment widths
   * differ per label ("Approved" vs "Approved with comments"), so the geometry
   * has to come from the DOM rather than be computed from the option list.
   * `useLayoutEffect` runs before paint, so the thumb never renders at a stale
   * position. A ResizeObserver re-measures when the bar wraps or the font
   * loads, both of which shift the segments.
   */
  useLayoutEffect(() => {
    const container = segmentsRef.current;
    if (!container) return undefined;

    const measure = () => {
      // Query the buttons rather than index `children` — the thumb is also a
      // child, so positional indexing would be off by one.
      const buttons = container.querySelectorAll<HTMLElement>('.bombiix-approval-bar__segment');
      const active = buttons[activeIndex];
      if (activeIndex < 0 || !active) {
        setThumb(null);
        return;
      }
      setThumb({ left: active.offsetLeft, width: active.offsetWidth });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [activeIndex, options]);

  const activeVariant = activeIndex >= 0 ? options[activeIndex]?.variant ?? 'green' : 'green';

  return (
    <div className={barClass}>
      {showStatusType && (
        <div className="bombiix-approval-bar__field">
          <NewSelectMultiSelect
            options={statusTypeOptions ?? []}
            value={statusType ?? null}
            onChange={(option) => {
              onStatusTypeChange?.((option as ApprovalSelectOption) ?? null);
            }}
            placeholder={statusTypePlaceholder}
            isDisabled={isDisabled}
            menuPlacement="top"
            menuPortalTarget={MENU_PORTAL_TARGET}
          />
        </div>
      )}

      <div className="bombiix-approval-bar__segments" role="group" ref={segmentsRef}>
        {/* The sliding fill. One element that moves between segments, so the
            selection glides instead of cross-fading. Hidden until measured and
            when nothing is selected. */}
        {thumb && (
          <span
            aria-hidden="true"
            className={`bombiix-approval-bar__thumb bombiix-approval-bar__thumb--${activeVariant}${isDisabled ? ' bombiix-approval-bar__thumb--disabled' : ''}`}
            style={{ transform: `translateX(${thumb.left}px)`, width: `${thumb.width}px` }}
          />
        )}
        {options.map((option) => {
          const isActive = value === option.value;
          const segmentClass = ['bombiix-approval-bar__segment', isActive && 'bombiix-approval-bar__segment--active'].filter(Boolean).join(' ');

          return (
            <button
              key={option.value}
              type="button"
              className={segmentClass}
              aria-pressed={isActive}
              disabled={isDisabled}
              onClick={() => onChange?.(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {showDatePicker && (
        <div className="bombiix-approval-bar__field">
          <NewDatePicker
            value={date ?? null}
            onChange={(newDate) => {
              onDateChange?.(newDate);
            }}
            placeholder={datePlaceholder}
            disabled={isDisabled}
            popperPlacement="top-start"
            portalId={DATE_PORTAL_ID}
            autoApply
          />
        </div>
      )}

      {showSupplier && (
        <div className="bombiix-approval-bar__field">
          <NewSelectMultiSelect
            options={supplierOptions ?? []}
            value={supplier ?? null}
            onChange={(option) => {
              onSupplierChange?.((option as ApprovalSelectOption) ?? null);
            }}
            placeholder={supplierPlaceholder}
            isDisabled={isDisabled}
            menuPlacement="top"
            menuPortalTarget={MENU_PORTAL_TARGET}
          />
        </div>
      )}

      {showComment && (
        <div className="bombiix-approval-bar__comment">
          <NewInput
            value={comment}
            maxLength={commentMaxLength}
            placeholder={commentPlaceholder}
            disabled={isDisabled}
            invalid={commentInvalid === true}
            onChange={(event) => onCommentChange?.(event.target.value)}
            endAdornment={showCommentCounter ? <span className="bombiix-input__counter">{`${comment.length}/${commentMaxLength}`}</span> : undefined}
          />
        </div>
      )}

      <button type="button" className="bombiix-approval-bar__save" aria-label="Save" disabled={isDisabled || saveDisabled === true} onClick={() => onSave?.()}>
        <SaveIcon size={28} />
      </button>
    </div>
  );
}

export default BombiixApprovalBar;
